import QRCode from "qrcode";
import { SITE_ORIGIN } from "@/lib/share";

export const WAVE_CAMPAIGN = "know-her-name";
export const QR_PLACEMENTS = ["stadium", "program", "sideline", "event", "social", "email"] as const;
export const QR_DESTINATIONS = ["campaign", "roster", "athlete"] as const;

export type QrPlacement = (typeof QR_PLACEMENTS)[number];
export type QrDestination = (typeof QR_DESTINATIONS)[number];

export type ResolvedWaveQr = {
  code_id: string;
  destination_type: string;
  placement: string;
  campaign: string;
  athlete_id: string | null;
  athlete_slug: string | null;
};

export const qrFallbackUrl = (destination: QrDestination, code: string, origin = SITE_ORIGIN): string => {
  const safeCode = encodeURIComponent(code);
  if (destination === "athlete") return `${origin}/q/wave/athlete/${safeCode}`;
  const path = destination === "roster" ? "/q/wave/roster" : "/q/wave";
  return `${origin}${path}?c=${safeCode}`;
};

/**
 * Origin used for on-screen test links only. On the live domain this is the
 * production origin; anywhere else (preview) it is the current origin so an
 * admin can scan/click and verify the redirect before publishing.
 * Printed/downloaded assets always use SITE_ORIGIN.
 */
export const runtimeQrOrigin = (): string =>
  typeof window === "undefined" ? SITE_ORIGIN : window.location.origin;

/** Link an admin can follow in the current environment (preview or live). */
export const qrTestUrl = (destination: QrDestination, code: string): string =>
  qrFallbackUrl(destination, code, runtimeQrOrigin());

/** Canonical URL encoded into every downloaded print-ready QR file. */
export const qrPrintUrl = (destination: QrDestination, code: string): string =>
  qrFallbackUrl(destination, code, SITE_ORIGIN);

/** First characters of an opaque code, for compact display only. */
export const shortCode = (code: string, length = 8): string => code.slice(0, length);

/** Compact, human-readable form of a print URL (no scheme, truncated code). */
export const shortQrUrl = (destination: QrDestination, code: string): string => {
  const full = qrPrintUrl(destination, code).replace(/^https?:\/\//, "");
  return full.replace(code, `${shortCode(code)}…`);
};

export const qrDestinationPath = (resolved: ResolvedWaveQr): string | null => {
  if (resolved.destination_type === "campaign") return "/wave";
  if (resolved.destination_type === "roster") return "/wave#roster";
  if (resolved.destination_type === "athlete" && resolved.athlete_slug) {
    return `/athlete/${encodeURIComponent(resolved.athlete_slug)}`;
  }
  return null;
};

const QR_OPTIONS = { errorCorrectionLevel: "H" as const, margin: 4, width: 1200, color: { dark: "#000000", light: "#ffffff" } };

export const makeQrSvg = (url: string): Promise<string> => QRCode.toString(url, { ...QR_OPTIONS, type: "svg" });
export const makeQrPng = (url: string): Promise<string> => QRCode.toDataURL(url, { ...QR_OPTIONS, type: "image/png" });

export const downloadDataUrl = (dataUrl: string, filename: string): void => {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  link.rel = "noopener";
  link.click();
};

export const downloadSvg = (svg: string, filename: string): void => {
  const blobUrl = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  downloadDataUrl(blobUrl, filename);
  URL.revokeObjectURL(blobUrl);
};
